// src/pages/settings/create-organization/orderQuote.ts
// The monthly bill is seats times the plan, plus the extensions that are turned on.
// A seat is a salesperson or an admin.
//
// [PRICING] THE ONE PLACE THE MONTHLY BILL IS COMPUTED. Read this before touching Stripe.
// Formula (per month, in MXN):
//   seats      = asesores + admins                  (seatCount below; empresas/proyectos/equipos are NOT billed here)
//   plan       = seats x perSeat                    (perSeat = seatPrice(book, plan, cycle): the book's plan row for the period)
//   extras     = sum of the turned-on extensions    (extensionCharges below; every unit is the book's row, priceBook.ts)
//   total      = plan + extras
// Example: 12 asesores + 2 admins on Base, monthly -> 14 x 299 = 4,186 plan. Add 12 phone lines (12 x 99 = 1,188) -> 5,374 total.
//
// [PRICING] PAID UP FRONT (cycle = semester / annual). The months are charged in one payment today:
//   months     = 6 or 12                            (CYCLE_MONTHS)
//   planList   = seats x listPerSeat x months       (what the seats would cost at the monthly price)
//   planDue    = plan x months                      (same seats at the cheaper up-front price)
//   discount   = planList - planDue                 (shown as "Descuento -NN%"; only the seats get the discount)
//   extrasDue  = extras x months                    (extensions are paid up front too, at their normal price)
//   dueNow     = planDue + extrasDue                (the number on the checkout button bar)
// Example: 11 seats on Base, annual -> list 11 x 299 x 12 = 39,468, discount -33% = -13,200, plan 11 x 199 x 12 = 26,268.
// On monthly, months = 1, so dueNow = total.
//
// [PRICING] COMMISSIONABLE REVENUE (partner program) = `plan` ONLY. It is the monthly seat cost and it
// does NOT include `extras` (the add-ons/extensions). In the returned quote that is `quote.plan`, never `quote.total`.
// The partner commission (50%) is applied to `quote.plan`. If the partner page copy and this number ever disagree, this file wins.
//
// [PRICING] PLAN IDS vs NAMES. The code ids are free / base / pro. The names the person sees come from
// translations: free = "Plan Light", base = "Plan Base", pro = "Plan Elite" (settings.upgrade.plans.<id>.name).
// [PRICING] Every price here is a row of the server's price book read through priceBook.ts (S-9): nothing is typed, and a lookup
//   that answers null is THE FAILED READ for the screen that needed it, never a number of its own. The formulas above stay.
//
// [HANDOFF] WHERE PRICES ARE SHOWN (all read this file; if a new screen shows a price, it must too):
//   Builder "Estimado" badge      structurePriceRange (structureDraft.ts)       per month, wide range
//   Plan cards + bottom bar       CreateOrganizationPlanPage.tsx                cards per person per month; bar = seats for the cycle
//   Extensions bottom bar         CreateOrganizationExtensionsPage.tsx          extensions only, for the cycle
//   Account "guardado" card       OnboardingAccountPage.tsx                     total for the cycle ("a pagar hoy" when up front)
//   Checkout                      CreateOrganizationBillingPage.tsx             full breakdown, dueNow
//   Settings plan cards           SettingsStyledPage.tsx                        per person per month        [NOT IN THIS PUSH]
//   Upgrade plan page             UpgradePlanPage.tsx                           per person per month + cycle total per person [NOT IN THIS PUSH]
//   Partner page (socios)         public/SociosPage.tsx                         TYPED by hand ($299 / $599 / $99 and the example); keep in sync
//
// [HANDOFF] Stripe line items must follow the chosen QUANTITIES (extensionCharges), not the structure counts.
// 3 advisors does not mean 3 WhatsApp APIs. One line item per extension x its qty, plus one for the seats at perSeat.
import type { StructureDraft } from '../../misc/onboarding/structureDraft';
import { type BillingCycle, type OrgAddons, type OrgPlanId, type OrgSetup } from './orgSetup';
import {
  type ExtensionId,
  type ExtensionScope,
  type PeriodMonths,
  type PriceBook,
  extensionRow,
  planRow,
} from '../../misc/onboarding/priceBook';

export type { BillingCycle };

/** His billing cycle onto the book's period in months (the server's domain). */
export const CYCLE_MONTHS: Record<BillingCycle, PeriodMonths> = { monthly: 1, semester: 6, annual: 12 };

/** The saved billing choice. Old saves only have `yearly`, so that is the fallback. */
export function billingCycle(setup: Pick<OrgSetup, 'cycle' | 'yearly'>): BillingCycle {
  return setup.cycle ?? (setup.yearly ? 'annual' : 'monthly');
}

/** Per seat per month for a plan on a billing cycle: the book's plan row for the period (S-9). null = THE FAILED READ. */
export function seatPrice(book: PriceBook, planId: OrgPlanId, cycle: BillingCycle): number | null {
  const row = planRow(book, planId, CYCLE_MONTHS[cycle]);
  return row === null ? null : row.perSeatPerMonthMxn;
}

/** [PRICING] [HANDOFF] RULE FOR EVERY SCREEN: a total next to a price label is what is paid for the chosen cycle.
 * Monthly -> per month ("al mes"). 6 months / annual -> the whole period ("por 6 meses" / "por 12 meses"), and at
 * account + checkout "a pagar hoy". Only per-person card prices stay per month (with the "-NN%" badge).
 * This returns the translation key for the label next to a period total. */
export function cycleTotalLabelKey(cycle: BillingCycle): string {
  if (cycle === 'annual') return 'settings.createOrganization.per12Months';
  if (cycle === 'semester') return 'settings.createOrganization.per6Months';
  return 'settings.createOrganization.perMonth';
}

/** The "-NN%" badge: how much cheaper the seat is than on the monthly row. 0 on monthly. */
const pctBelowList = (listPerSeat: number, perSeat: number) => Math.round((1 - perSeat / listPerSeat) * 100);

/** The "-NN%" badge for a plan on a billing cycle, from the book's monthly and period rows. null = THE FAILED READ. */
export function cycleDiscountPct(book: PriceBook, planId: OrgPlanId, cycle: BillingCycle): number | null {
  const listPerSeat = seatPrice(book, planId, 'monthly');
  const perSeat = seatPrice(book, planId, cycle);
  return listPerSeat === null || perSeat === null ? null : pctBelowList(listPerSeat, perSeat);
}

// [PRICING] "Por organización" extensions (WhatsApp API, Zoom) are one for the whole organization. The internal ids still say
// company (waCompany, videoScope 'company', zoomCompany). Raise this if an organization should ever buy more than one.
export const ORG_EXTENSION_MAX = 1;

// [PRICING] Billable seats = advisors + admins. Companies, projects and teams are free; they only cap some extensions.
export function seatCount(draft: StructureDraft): number {
  return Math.max(0, (draft.asesores || 0) + (draft.admins || 0));
}

// [PRICING] Sellers = advisors only (admins do not sell). This is the max quantity for per-person extensions.
export function sellerCount(draft: StructureDraft): number {
  return Math.max(0, draft.asesores || 0);
}

// [PRICING] One extension unit per month for a plan on a billing cycle: the book's row for his charge id, the plan and the period
// (THE ROW MAP in priceBook.ts; voz_basica is dearer on Light by its own row). null = THE FAILED READ, never a number of its own.
export function extensionUnit(book: PriceBook, id: ExtensionId, planId: OrgPlanId, cycle: BillingCycle): number | null {
  const row = extensionRow(book, id, planId, CYCLE_MONTHS[cycle]);
  return row === null ? null : row.perUnitPerMonthMxn;
}

// [PRICING] Voice clone per seat per month: the plan's voz_basica row (dearer on Light than on paid plans).
export function voiceUnit(book: PriceBook, planId: OrgPlanId, cycle: BillingCycle): number | null {
  return extensionUnit(book, 'voice', planId, cycle);
}

const within = (qty: number, max: number) => Math.min(Math.max(0, qty || 0), Math.max(0, max));

/** One extension line: his id, the book's row_key and scope P2 sends, the capped quantity, the unit per month and amount = quantity x unit. */
export type ExtensionCharge = { id: ExtensionId; rowKey: string; scope: ExtensionScope; quantity: number; unit: number; amount: number };

// [PRICING] One line per turned-on extension: amount = chosen qty (capped by `max`) x unit price, the unit the book's row for the
// plan and period (S-9), so the body sent to the server carries the quantity and never recomputes a cap.
// Caps: per-person extensions <= advisors, per-team <= teams, per-organization = ORG_EXTENSION_MAX (1, even with several empresas).
// Voice and Voice Pro only exist when the phone extension is on, and their qty cannot exceed the phone lines BILLED - the phone qty
// inside the sellers, computed once before the pushes - so a saved phone qty the structure outgrew never bills more voice than lines.
// Voice Pro is not offered on Light. These `id`s are what the checkout page labels; `rowKey` is what the Stripe line items map to.
// The per-team WhatsApp API row is not sold on the screen (P-4): waTeam stays false and has no row in the map, so no line is pushed.
// null = THE FAILED READ: a turned-on extension whose row the book lacks, so no quote is a number of its own.
export function extensionCharges(
  book: PriceBook,
  draft: StructureDraft,
  addons: OrgAddons,
  planId: OrgPlanId,
  cycle: BillingCycle,
): ExtensionCharge[] | null {
  const sellers = sellerCount(draft);
  const teams = Math.max(0, draft.equipos || 0);
  const companies = ORG_EXTENSION_MAX;
  const months = CYCLE_MONTHS[cycle];
  const lines: ExtensionCharge[] = [];
  let failed = false;
  const push = (id: ExtensionId, on: boolean, qty: number, max: number) => {
    if (!on) return;
    const row = extensionRow(book, id, planId, months);
    if (row === null) {
      failed = true;
      return;
    }
    const unit = row.perUnitPerMonthMxn;
    if (unit <= 0) return;
    const quantity = within(qty, max);
    const amount = quantity * unit;
    if (amount > 0) lines.push({ id, rowKey: row.rowKey, scope: row.scope, quantity, unit, amount });
  };
  // [PRICING] The phone line as billed - the chosen qty inside the sellers - caps voice and voice pro, never the chosen qty itself:
  // a saved setup whose sellers fell after the extensions were chosen bills no more voice seats than phone lines (OPEN (12)).
  const phoneLines = within(addons.phoneQty, sellers);
  push('phone', addons.phone, addons.phoneQty, sellers);
  if (addons.phone) {
    push('voice', addons.voice, addons.voiceQty, phoneLines);
    if (planId !== 'free') push('voicePro', addons.voicePro, addons.voiceProQty, phoneLines);
  }
  push('waPersonal', addons.waSeller, addons.waSellerQty, sellers);
  push('waCompany', addons.waCompany, addons.waCompanyQty || 1, companies);
  if (addons.videoIncluded && addons.videoScope === 'person') {
    push('zoom', true, addons.videoQty, sellers);
  }
  if (addons.videoIncluded && addons.videoScope === 'team') {
    push('zoomTeam', true, addons.videoQty, teams);
  }
  if (addons.videoIncluded && addons.videoScope === 'company') {
    push('zoomCompany', true, addons.videoQty, companies);
  }
  if (addons.videoOwn) push('zoomOwn', true, 1, 1);
  return failed ? null : lines;
}

// [HANDOFF] THE LINE LABELS (WU-142 UNIT 8 CHK4; docs/manifest/WU-142.md SESSION 45 RECORD THE LINE LABELS FINDING, self-stamp
// 146): the translation key of each charge id above - the one table the checkout and the resume page both read, so one row is never
// named two ways. Typed by the book's ExtensionId: every id has its label, and waTeam, which is never pushed (P-4), has none.
export const LINE_LABEL_KEYS: Readonly<Record<ExtensionId, string>> = {
  phone: 'settings.createOrganization.phoneItem',
  voice: 'settings.createOrganization.voiceItem',
  voicePro: 'settings.createOrganization.voicePro',
  waPersonal: 'settings.createOrganization.waSeller',
  waCompany: 'settings.createOrganization.waCompany',
  zoom: 'settings.createOrganization.zoomByPerson',
  zoomTeam: 'settings.createOrganization.zoomByTeam',
  zoomCompany: 'settings.createOrganization.zoomByCompany',
  zoomOwn: 'settings.createOrganization.videoOwn',
};

// [I18N] Output is "$1,234 MXN" in both languages; only the thousands separator changes (es-MX vs en-US).
export function formatMxn(n: number, language: string): string {
  const num = n.toLocaleString(language === 'es' ? 'es-MX' : 'en-US');
  return `$${num} MXN`;
}

/** The quote every pricing screen shows (his shape); null = THE FAILED READ for the screen (S-9), never a number of its own. */
export type OrderQuote = {
  seats: number;
  perSeat: number;
  listPerSeat: number;
  planId: OrgPlanId;
  cycle: BillingCycle;
  months: PeriodMonths;
  discountPct: number;
  plan: number;
  extras: number;
  lines: ExtensionCharge[];
  total: number;
  planList: number;
  planDiscount: number;
  planDue: number;
  extrasDue: number;
  dueNow: number;
  addons: OrgAddons;
};

// [PRICING] [HANDOFF] The single quote object. Plan page, extensions page, account page and checkout all call this.
// plan = commissionable revenue per month. extras = add-ons per month. total = plan + extras per month.
// dueNow = what Stripe charges today: total on monthly, the whole 6 or 12 months on semester / annual (see the block at the top).
// `lines` is the ready-made list of extension charges (per month) for Stripe line items; multiply by `months` when paid up front.
// The plan billed is the plan chosen (S-2; his default base); perSeat is the book's row for the period and listPerSeat its monthly row.
export function orderQuote(book: PriceBook, draft: StructureDraft, setup: OrgSetup): OrderQuote | null {
  const seats = seatCount(draft);
  const planId = setup.planId || 'base';
  const cycle = billingCycle(setup);
  const months = CYCLE_MONTHS[cycle];
  const listPerSeat = seatPrice(book, planId, 'monthly');
  const perSeat = seatPrice(book, planId, cycle);
  const lines = extensionCharges(book, draft, setup.addons, planId, cycle);
  if (listPerSeat === null || perSeat === null || lines === null) return null;
  const plan = seats * perSeat;
  const extras = lines.reduce((sum, line) => sum + line.amount, 0);
  const planList = seats * listPerSeat * months;
  const planDue = plan * months;
  const extrasDue = extras * months;
  return {
    seats,
    perSeat,
    listPerSeat,
    planId,
    cycle,
    months,
    discountPct: pctBelowList(listPerSeat, perSeat),
    plan,
    extras,
    lines,
    total: plan + extras,
    planList,
    planDiscount: planList - planDue,
    planDue,
    extrasDue,
    dueNow: planDue + extrasDue,
    addons: setup.addons,
  };
}
